import React, { useContext, useState } from "react";
import { observer } from "mobx-react";
import { Dialog } from "primereact/dialog";
import { DeviceTypeCategory } from "../../../../api/firebase/types/device.types";
import ToastContext from "../../../../components/toast/ToastContext";
import DeviceStore from "../../../../store/DeviceStore";
import { DeviceToasts } from "../../../../toastMessages/deviceToastMessages";
import DeviceSelectButton from "./fields/DeviceSelectButton";
import SaveCancelFooter from "../../../../components/footer/SaveCancelFooter";
import AddHardwareDeviceForm, { DeviceFormData } from "./AddHardwareDeviceForm";
import AddDesktopClientForm, {
  DesktopClientFormData,
} from "./AddDesktopClientForm";
import { DesktopUserCreationStatus } from "../../../../api/firebase/functionCallables/desktopUserCallables";
import { ProgressBar } from "primereact";

export type AddDeviceDialogType = {
  isVisible: boolean;
  closeModal: () => void;
};

const AddDeviceDialog: React.FC<AddDeviceDialogType> = (props) => {
  const [deviceType, setDeviceType] = useState(
    DeviceTypeCategory.HARDWARE_DEVICE
  );
  const [showLoader, setShowLoader] = useState(false);

  const showToast = useContext(ToastContext);

  const onClose = () => {
    setDeviceType(DeviceTypeCategory.HARDWARE_DEVICE);
    props.closeModal();
  };

  const onSave = async (
    data: DeviceFormData | DesktopClientFormData,
    status?: DesktopUserCreationStatus
  ) => {
    if (deviceType === DeviceTypeCategory.HARDWARE_DEVICE)
      await createHardwareDevice(data as DeviceFormData);
    else await createDesktopClient(data as DesktopClientFormData, status!);
    setShowLoader(false);
  };

  const createHardwareDevice = async (data: DeviceFormData) => {
    const { deviceCreated, deviceCreationError } = DeviceToasts;
    const creationSucceeded = await DeviceStore.addDevice(data);
    showToast(creationSucceeded ? deviceCreated : deviceCreationError);
    if (creationSucceeded) {
      onClose();
    }
  };

  const createDesktopClient = async (
    data: DesktopClientFormData,
    status: DesktopUserCreationStatus
  ) => {
    const { deviceCreated, deviceCreationError } = DeviceToasts;
    if (status === "error") {
      showToast(DeviceToasts.desktopUserCreationError);
    } else {
      const creationSucceeded = await DeviceStore.addDesktopClient(data);
      if (!creationSucceeded) {
        showToast(deviceCreationError);
        return;
      }
      showToast(deviceCreated);
      onClose();
    }
  };

  const renderDeviceForm = () =>
    deviceType === DeviceTypeCategory.HARDWARE_DEVICE ? (
      <AddHardwareDeviceForm onSave={onSave} showLoader={setShowLoader} />
    ) : (
      <AddDesktopClientForm onSave={onSave} showLoader={setShowLoader} />
    );

  return (
    <Dialog
      visible={props.isVisible}
      header={
        <>
          {showLoader && (
            <ProgressBar className="p-mb-2" mode="indeterminate" />
          )}
          <div className="p-dialog-title">Meldeinstanz hinzufügen</div>
        </>
      }
      modal
      className="p-fluid"
      footer={() => (
        <SaveCancelFooter
          formId="device-creation-modal"
          cancelAction={props.closeModal}
        />
      )}
      onHide={onClose}
      style={{ width: "400px" }}
    >
      <div className="p-formgrid p-grid">
        <div className="p-col-12">
          <DeviceSelectButton
            deviceType={deviceType}
            setDeviceType={setDeviceType}
          />
        </div>
        {renderDeviceForm()}
      </div>
    </Dialog>
  );
};

export default observer(AddDeviceDialog);
